import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, Package, Loader2, CheckCircle, Clock, Truck, 
  CreditCard, FileText, ArrowLeft, User
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TrackOrderPage = () => {
  const { user, isAuthenticated } = useAuth();
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setEmail(user.email);
    }
  }, [isAuthenticated, user]);

  const handleTrack = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setOrder(null);

    try {
      const response = await axios.post(`${API}/orders/track`, {
        order_id: orderId.trim(),
        email: email.trim()
      });
      
      setOrder(response.data.order);
      toast.success('Order found!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Order not found. Please check your details.';
      setError(message);
      toast.error(message);
    }
    setLoading(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      'pending_invoice': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
      'confirmed': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'printing': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      'shipped': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
      'completed': 'bg-green-500/10 text-green-400 border-green-500/20',
      'cancelled': 'bg-red-500/10 text-red-400 border-red-500/20'
    };
    return colors[status] || 'bg-white/10 text-white/70 border-white/20';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'paid': 'bg-green-500/10 text-green-400 border-green-500/20',
      'pending': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      'invoice_pending': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
      'failed': 'bg-red-500/10 text-red-400 border-red-500/20'
    };
    return colors[status] || 'bg-white/10 text-white/70 border-white/20';
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="overline text-[#CCFF00]">Order Tracking</span>
            <h1 className="text-4xl font-['Chivo'] font-black text-white mt-2">
              Track Your Order
            </h1>
            <p className="text-white/60 mt-4">
              {isAuthenticated 
                ? 'Enter your order ID to track your order status'
                : 'Enter your order ID and email address to track your order status'
              }
            </p>
          </div>

          {/* Track Form */}
          <div className="bg-[#111] border border-white/10 rounded-sm p-8 mb-8">
            <form onSubmit={handleTrack} className="space-y-6">
              <div>
                <label className="block text-white/70 text-sm mb-2">Order ID</label>
                <Input
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value.toUpperCase())}
                  placeholder="e.g., ORD-ABC12345"
                  className="bg-white/5 border-white/10 text-white"
                  required
                />
                <p className="text-white/40 text-xs mt-1">
                  You can find this in your order confirmation email
                </p>
              </div>

              {!isAuthenticated && (
                <div>
                  <label className="block text-white/70 text-sm mb-2">Email Address</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="The email used when placing the order"
                    className="bg-white/5 border-white/10 text-white"
                    required
                  />
                </div>
              )}

              {isAuthenticated && (
                <div className="bg-[#CCFF00]/10 border border-[#CCFF00]/20 rounded-sm p-3">
                  <p className="text-[#CCFF00] text-sm flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Tracking as {user?.email}
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold py-6"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Track Order
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Order Result */}
          {order && (
            <div className="bg-[#111] border border-white/10 rounded-sm overflow-hidden">
              {/* Order Header */}
              <div className="p-6 border-b border-white/10">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white/50 text-sm">Order</p>
                    <p className="text-white font-mono text-xl font-bold">{order.order_id}</p>
                    <p className="text-white/50 text-sm mt-1">
                      {new Date(order.created_at).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/50 text-sm mb-2">Status</p>
                    <Badge variant="outline" className={`text-sm ${getStatusColor(order.order_status)}`}>
                      {formatStatus(order.order_status)}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Status Timeline */}
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className={`flex flex-col items-center ${['pending', 'pending_invoice', 'confirmed', 'printing', 'shipped', 'completed'].includes(order.order_status) ? 'text-[#CCFF00]' : 'text-white/30'}`}>
                    <div className="w-10 h-10 rounded-full border-2 flex items-center justify-center mb-2">
                      <FileText className="w-5 h-5" />
                    </div>
                    <span className="text-xs">Received</span>
                  </div>
                  <div className={`flex-1 h-0.5 mx-2 ${['confirmed', 'printing', 'shipped', 'completed'].includes(order.order_status) ? 'bg-[#CCFF00]' : 'bg-white/10'}`}></div>
                  <div className={`flex flex-col items-center ${['confirmed', 'printing', 'shipped', 'completed'].includes(order.order_status) ? 'text-[#CCFF00]' : 'text-white/30'}`}>
                    <div className="w-10 h-10 rounded-full border-2 flex items-center justify-center mb-2">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <span className="text-xs">Confirmed</span>
                  </div>
                  <div className={`flex-1 h-0.5 mx-2 ${['printing', 'shipped', 'completed'].includes(order.order_status) ? 'bg-[#CCFF00]' : 'bg-white/10'}`}></div>
                  <div className={`flex flex-col items-center ${['printing', 'shipped', 'completed'].includes(order.order_status) ? 'text-[#CCFF00]' : 'text-white/30'}`}>
                    <div className="w-10 h-10 rounded-full border-2 flex items-center justify-center mb-2">
                      <Package className="w-5 h-5" />
                    </div>
                    <span className="text-xs">Printing</span>
                  </div>
                  <div className={`flex-1 h-0.5 mx-2 ${['shipped', 'completed'].includes(order.order_status) ? 'bg-[#CCFF00]' : 'bg-white/10'}`}></div>
                  <div className={`flex flex-col items-center ${['shipped', 'completed'].includes(order.order_status) ? 'text-[#CCFF00]' : 'text-white/30'}`}>
                    <div className="w-10 h-10 rounded-full border-2 flex items-center justify-center mb-2">
                      <Truck className="w-5 h-5" />
                    </div>
                    <span className="text-xs">Shipped</span>
                  </div>
                  <div className={`flex-1 h-0.5 mx-2 ${order.order_status === 'completed' ? 'bg-[#CCFF00]' : 'bg-white/10'}`}></div>
                  <div className={`flex flex-col items-center ${order.order_status === 'completed' ? 'text-[#CCFF00]' : 'text-white/30'}`}>
                    <div className="w-10 h-10 rounded-full border-2 flex items-center justify-center mb-2">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <span className="text-xs">Complete</span>
                  </div>
                </div>
              </div>

              {/* Order Details */}
              <div className="p-6 border-b border-white/10">
                <h3 className="text-white font-bold mb-4">Order Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/50 text-sm">Customer</p>
                    <p className="text-white">{order.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Payment Method</p>
                    <p className="text-white capitalize">{order.payment_method}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Payment Status</p>
                    <Badge variant="outline" className={`text-xs ${getPaymentStatusColor(order.payment_status)}`}>
                      {formatStatus(order.payment_status)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Total</p>
                    <p className="text-[#CCFF00] font-bold">${order.total?.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="p-6">
                <h3 className="text-white font-bold mb-4">Items ({order.items?.length || 0})</h3>
                <div className="space-y-3">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/5 rounded flex items-center justify-center">
                          <Package className="w-5 h-5 text-[#CCFF00]" />
                        </div>
                        <div>
                          <p className="text-white text-sm">{item.file_name}</p>
                          <p className="text-white/50 text-xs">Qty: {item.quantity}</p>
                        </div>
                      </div>
                      <p className="text-white font-mono">${(item.total_price || item.price || item.unit_price || 0).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="mt-8 text-center">
            <p className="text-white/50 mb-4">
              {isAuthenticated 
                ? 'Need help with your order?'
                : 'Need help with your order? Have an account?'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {!isAuthenticated && (
                <Link to="/login">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    <User className="w-4 h-4 mr-2" />
                    Sign In to Dashboard
                  </Button>
                </Link>
              )}
              {isAuthenticated && (
                <Link to="/dashboard">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    <User className="w-4 h-4 mr-2" />
                    Back to Dashboard
                  </Button>
                </Link>
              )}
              <Link to="/">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TrackOrderPage;
